// Users admin screen: displays and manages admin users.
// Implements loading, filtering and actions for the Users tab.
import { Component, OnInit, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CopilotApiService } from '../../services/copilot-api-service';
import { User } from '../../model/user';
import { BusinessPartner } from '../../model/business_partner';
import { AuthService } from '@auth0/auth0-angular';
import { AddUserDialog } from './add-user-dialog';
import { ToastService } from '../../services/toast.service';
import { BusinessPartnerSelector } from '../../common/components/business-partner-selector/business-partner-selector';

@Component({
  selector: 'app-users',
  standalone: true,
  imports: [CommonModule, TranslateModule, AddUserDialog, BusinessPartnerSelector],
  templateUrl: './users.html',
  styleUrl: './users.css'
})
// Angular standalone component for the Users admin screen.
// Coordinates API calls, signals, and UI interactions.
export class Users implements OnInit {
  // Holds the full users list, loading and UI state for the screen.
  // Derived state is computed via signals to keep the view in sync.
  protected readonly users = signal<User[] | null>(null);
  protected readonly loading = signal<boolean>(false);
  protected readonly error = signal<string | null>(null);
  protected readonly showAddDialog = signal<boolean>(false);
  protected readonly submitting = signal<boolean>(false);
  protected readonly showDisabled = signal<boolean>(false);
  protected readonly selectedBP = signal<BusinessPartner | null>(null);
  protected readonly searchTerm = signal<string>('');
  protected readonly sortKey = signal<'Name' | 'BusinessPartner' | 'Parent' | 'Level' | 'CopilotEnabled'>('Name');
  protected readonly sortDir = signal<'asc' | 'desc'>('asc');
  protected readonly filteredUsers = computed(() => {
    const list = this.users() || [];
    const byEnabled = this.showDisabled() ? list : list.filter(u => (u.user_metadata?.CopilotEnabled === 1));
    const q = this.searchTerm().trim().toLowerCase();
    const bySearch = q
      ? byEnabled.filter(u => {
          const email = String(u.email || '').toLowerCase();
          const name = String(u.name || '').toLowerCase();
          const first = String(u.user_metadata?.firstName || '').toLowerCase();
          const last = String(u.user_metadata?.lastName || '').toLowerCase();
          const bp = String(u.user_metadata?.BusinessPartner || '').toLowerCase();
          const parent = String(u.user_metadata?.Parent || '').toLowerCase();
          return email.includes(q) || name.includes(q) || first.includes(q) || last.includes(q) || bp.includes(q) || parent.includes(q);
        })
      : byEnabled;
    const sel = this.selectedBP();
    if (!sel) {
      // no BP selection; return sorted result
      return [...bySearch].sort((a, b) => this.compareUsers(a, b));
    }
    const selId = sel.Id;
    const selName = String(sel.CompanyName || sel.Name || '').toLowerCase();
    const byBp = bySearch.filter(u => {
      const anyU: any = u as any;
      const meta: any = u.user_metadata as any;
      const uBpId = anyU?.BusinessPartnerId ?? meta?.BusinessPartnerId;
      if (uBpId != null && selId != null) {
        return Number(uBpId) === Number(selId);
      }
      const uBpName = String(u.user_metadata?.BusinessPartner || '').toLowerCase();
      return !!uBpName && uBpName === selName;
    });
    return [...byBp].sort((a, b) => this.compareUsers(a, b));
  });

  // Initializes services and reactive streams for the Users screen.
  // Injected services provide API, auth, i18n, and notifications.
  constructor(private api: CopilotApiService, private auth: AuthService, private translate: TranslateService, private toast: ToastService) {}

  // Kicks off initial data load when the component initializes.
  // Subscribes to user profile to fetch the user list.
  ngOnInit(): void {
    // Load sort config (default: Name asc)
    try {
      const raw = localStorage.getItem('users:sortConfig');
      if (raw) {
        const parsed = JSON.parse(raw) as { key?: 'Name'|'BusinessPartner'|'Parent'|'Level'|'CopilotEnabled'; dir?: 'asc'|'desc' };
        if (parsed?.key) { this.sortKey.set(parsed.key); }
        if (parsed?.dir) { this.sortDir.set(parsed.dir); }
      }
    } catch {}
    this.loadUsers();
  }

  // Updates the selected Business Partner used by the users filter.
  // Triggered by the Business Partner selector in the toolbar.
  protected onBusinessPartnerChange(bp: BusinessPartner | null): void {
    this.selectedBP.set(bp);
  }

  protected onSearchChange(term: string): void {
    this.searchTerm.set(String(term || ''));
  }

  protected clearSearch(): void {
    this.searchTerm.set('');
  }

  protected onSort(key: 'Name' | 'BusinessPartner' | 'Parent' | 'Level' | 'CopilotEnabled'): void {
    const nextDir: 'asc' | 'desc' = this.sortKey() === key ? (this.sortDir() === 'asc' ? 'desc' : 'asc') : 'asc';
    this.sortKey.set(key);
    this.sortDir.set(nextDir);
    try {
      localStorage.setItem('users:sortConfig', JSON.stringify({ key, dir: nextDir }));
    } catch {}
  }

  private compareUsers(a: User, b: User): number {
    const key = this.sortKey();
    const dir = this.sortDir();
    let av: string | number = '';
    let bv: string | number = '';
    switch (key) {
      case 'Name':
        av = String(a.name || '').toLowerCase();
        bv = String(b.name || '').toLowerCase();
        break;
      case 'BusinessPartner':
        av = String(a.user_metadata?.BusinessPartner || '').toLowerCase();
        bv = String(b.user_metadata?.BusinessPartner || '').toLowerCase();
        break;
      case 'Parent':
        av = String(a.user_metadata?.Parent || '').toLowerCase();
        bv = String(b.user_metadata?.Parent || '').toLowerCase();
        break;
      case 'Level':
        av = Number(a.user_metadata?.Level ?? 0);
        bv = Number(b.user_metadata?.Level ?? 0);
        break;
      case 'CopilotEnabled':
        av = (a.user_metadata?.CopilotEnabled === 1) ? 1 : 0;
        bv = (b.user_metadata?.CopilotEnabled === 1) ? 1 : 0;
        break;
    }
    let cmp = 0;
    if (av < bv) cmp = -1;
    else if (av > bv) cmp = 1;
    return dir === 'desc' ? -cmp : cmp;
  }


  // Loads users from the API using current user's metadata context.
  // Updates loading/error state and populates the users signal.
  private loadUsers(): void {
    this.loading.set(true);
    this.error.set(null);
    // Prefer current user's metadata if available; otherwise fall back to defaults
    this.auth.user$.subscribe((me) => {
      const meta: any = me ? (me as any)['user_metadata'] : undefined;
      const bp = meta?.BusinessPartner ?? 'FleetBooster';
      const parent = meta?.Parent ?? 'FleetBooster';
      const level = meta?.Level ?? 0;
      this.api.getAdminUsers('', bp, parent, level).subscribe({
        next: (list) => {
          this.users.set(list || []);
          this.loading.set(false);
        },
        error: (err) => {
          this.error.set(this.translate.instant('users.error_load'));
          this.loading.set(false);
          console.error(err);
        }
      });
    });
  }

  // Toggles Copilot access for a specific user.
  // Optimistically updates local state after a successful API call.
  protected toggleCopilot(user: User): void {
    const current = user.user_metadata?.CopilotEnabled === 1 ? 1 : 0;
    const next = current === 1 ? 0 : 1;
    this.api.changeAdminUserStatus({ user_id: user.user_id, action: next }).subscribe({
      next: () => {
        // update local state
        const updated = (this.users() || []).map(u =>
          u.user_id === user.user_id
            ? { ...u, user_metadata: { ...u.user_metadata, CopilotEnabled: next } }
            : u
        );
        this.users.set(updated);
      },
      error: (err) => {
        console.error(err);
        this.error.set(this.translate.instant('users.error_change_status'));
      }
    });
  }

  // Opens the add user dialog.
  // Used by the toolbar "Create user" button.
  protected openAddDialog(): void { this.showAddDialog.set(true); }
  // Closes the add user dialog.
  // Invoked after cancel or successful submission.
  protected closeAddDialog(): void { this.showAddDialog.set(false); }
  // Submits a new admin user to the API based on form input.
  // Refreshes the list and shows success or error toasts.
  protected submitNewUser(form: { name: string; lastName: string; email: string; businessPartner: BusinessPartner; password: string; }): void {
    // We need current user's profile for parent/level/createdBy/grandParent
    this.submitting.set(true);
    this.auth.user$.subscribe((me) => {
      const meta: any = me ? (me as any)['user_metadata'] : undefined;
      const parent = meta?.BusinessPartner ?? 'FleetBooster';
      const level = (meta?.Level ?? 0) + 1;
      const createdBy = me?.name ?? '';
      const grandParent = meta?.Parent ?? 'FleetBooster';

      this.api.addAdminUser({
        name: form.name,
        lastName: form.lastName,
        email: form.email,
        businessPartner: form.businessPartner.CompanyName,
        businessPartnerId: form.businessPartner.Id,
        password: form.password,
        parent,
        level,
        createdBy,
        grandParent
      }).subscribe({
        next: () => {
          this.closeAddDialog();
          this.loadUsers();
          this.toast.success(this.translate.instant('users.add_success_toast'), 3000);
          this.submitting.set(false);
        },
        error: (err) => {
          console.error(err);
          this.closeAddDialog();
          this.toast.error(this.translate.instant('users.add_error_toast'), 3000);
          this.submitting.set(false);
        }
      });
    });
  }
}
